<template>
    <div data-testid="workflow__root" class="pa-6 pb-16 d-flex flex-column gap-6">
        <!-- Bulk Edit Mode -->
        <WorkflowBulkEdit 
            v-if="isBulkEditMode" 
            :ids="selectedIds" 
            @back="closeBulkEdit" 
            class="pa-0"
        />

        <!-- List Mode -->
        <div v-else class="d-flex flex-column gap-6">
            <!-- Title -->
            <div class="d-flex align-center">
                <PageHeader 
                    :title="'Workflow Configuration'"
                    :loading="loading"
                    @refresh="refreshData({ forceRefresh: true })"
                />
                <v-spacer />
                <v-tooltip left content-class="tooltip-left">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                            icon 
                            class=""
                            v-bind="attrs"
                            v-on="on"
                            @click="isBulkUploadDialogVisible = true"
                            data-testid="access-management__bulk-upload-button"
                        >
                            <img src="/img/icons/BulkRUUpload.svg" alt="Bulk Upload">
                        </v-btn>
                    </template>
                    <span>Bulk Upload</span>
                </v-tooltip>
            </div>
            <!-- Filter -->
            <cfilter 
                cycletype="Actual" 
                :showrole="false" 
                :showyear="true" 
                :showperiod="true" 
                :shownetwork="true" 
                :showregion="true" 
                :showcountry="true" 
                :showreportingunit="true"
                :showSync="false"
                :showFavourite="false"
                :showReset="true"
                :showApply="true"
                @newdata="handleFilterChange"
            />
            <!-- Table -->
            <v-card flat class="pa-3 d-flex flex-column gap-2" outlined>
                <div class="d-flex align-center justify-end" v-if="selectedIds.length > 0">
                    <v-tooltip bottom content-class="tooltip-bottom">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn 
                                icon 
                                class="" 
                                @click="openBulkEdit"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <img src="/img/icons/PenEditIcon.svg" alt="Bulk Edit">
                            </v-btn>
                        </template>
                        <span>Bulk Edit</span>
                    </v-tooltip>
                </div>
                <WorkflowConfig 
                    :loading="loading" 
                    :items="enrichedItems" 
                    :page.sync="page"
                    :meta="meta || {}"
                    :perpage="perpage"
                    :resetKey="selectionResetKey"
                    @load-data="handleLoadData"
                    @selection-change="handleSelectionChange" 
                />
            </v-card>
            
            <WorkflowBulkUpload 
                v-model="isBulkUploadDialogVisible" 
                @success="handleBulkUploadSuccess" 
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import cfilter from '../Filter.vue';
import WorkflowConfig from './components/WorkflowConfigTable.vue';
import WorkflowBulkEdit from './components/WorkflowBulkEdit.vue';
import WorkflowBulkUpload from './components/WorkflowBulkUpload.vue';
import PageHeader from '../../components/PageHeader.vue';

export default {
    name: 'Workflow',
    components: {
        cfilter,
        WorkflowConfig,
        WorkflowBulkEdit,
        WorkflowBulkUpload,
        PageHeader
    },
    data() {
        return {
            loading: true,
            materialityLoading: false,
            isBulkEditMode: false,
            isBulkUploadDialogVisible: false,
            selectedIds: [],
            selectionResetKey: 0,
            page: 1,
            perpage: 25,
            filtersReady: false,
            pendingInitialLoad: true,
            filterDebounceTimer: null,
            tableFilters: {},
            sortKey: '',
            sortDesc: false,
            globalFilterState: {
                selectedyear: null,
                selectedperiod: null,
                selectednetwork: null,
                selectedregion: null,
                selectedcountry: null,
                selectedreportingunit: null,
                selectedReportingUnitType: null,
                selectedrole: null
            }
        };
    },
    computed: {
        ...mapGetters('varianceanalysis/reportingUnit', {
            rawItems: 'getMyReportingUnits',
            meta: 'getMyReportingUnitsMeta',
            materialityData: 'getMaterialityData'
        }),
        enrichedItems() {
            // Merge materialityData with rawItems
            if (!this.rawItems || this.rawItems.length === 0) return [];
            
            const materialityMap = new Map(
                (this.materialityData || []).map(item => [item._id, item])
            );
            
            return this.rawItems.map(item => {
                const materialityInfo = materialityMap.get(item._id);
                return {
                    ...item,
                    allusersset: item.allusersset, // Keep from getMyRus
                    materialityset_actual: materialityInfo?.materialityset_actual ?? false,
                    materialityset_forecast: materialityInfo?.materialityset_forecast ?? false
                };
            });
        }
    },
    methods: {
        ...mapActions('varianceanalysis/reportingUnit', ['fetchMyReportingUnits', 'fetchMateriality']),
        
        handleSelectionChange(ids) {
            this.selectedIds = ids || [];
        },

        openBulkEdit() {
            if (this.selectedIds.length === 0) return;
            this.isBulkEditMode = true;
        },

        closeBulkEdit() {
            this.isBulkEditMode = false;
            this.selectedIds = [];
            // Force refresh to show updated data after edit
            this.refreshData({ forceRefresh: true });
        },

        handleBulkUploadSuccess() {
            this.refreshData({ forceRefresh: true });
        },
        
        loadNextPage(val) {
            this.page = val;
            this.refreshData();
        },

        handleLoadData(payload) {
            const { page, filters, sortKey, sortDesc, perPage, isPagination } = payload;
            this.page = page || 1;
            this.tableFilters = filters || {};
            this.sortKey = sortKey || '';
            this.sortDesc = sortDesc || false;
            if (perPage) this.perpage = perPage;
            
            this.refreshData({ forceRefresh: true, isPagination: !!isPagination });
        },

        getMappedTableFilters() {
            const mapped = {};
            // Map frontend filter keys to backend expected keys
            const keyMap = {
                'allUsersSet': 'allusersset',
                'materialityActual': 'materialityset_actual',
                'materialityForecast': 'materialityset_forecast',
                
                // PL columns map to base names as per usage
                'localcontrol_proforma_pl': 'localcontrol_proforma',
                'localmanagement_proforma_pl': 'localmanagement_proforma',
                'regionalcontrol_proforma_pl': 'regionalcontrol_proforma',
                'regionalmanagement_proforma_pl': 'regionalmanagement_proforma',
                
                'localcontrol_forecast_pl': 'localcontrol_forecast',
                'localmanagement_forecast_pl': 'localmanagement_forecast',
                'regionalcontrol_forecast_pl': 'regionalcontrol_forecast',
                'regionalmanagement_forecast_pl': 'regionalmanagement_forecast',
                
                'autoSubmissionEnabled': 'auto_regional_commentary_submission_enabled'
            };

            Object.entries(this.tableFilters).forEach(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return;
                
                // If mapping exists, use it, otherwise use original key
                const backendKey = keyMap[key] || key;
                
                // Convert Yes/No to booleans if needed, or pass as array
                // Assuming backend takes array of strings or booleans
                // Let's try to convert 'Yes'/'No' to true/false if that's what backend wants
                // But header cell returns what is in the table (which is 'Yes'/'No' strings)
                // If backend expects booleans, we map:
                let backendValue = value;
                if (Array.isArray(value)) {
                    const hasYes = value.includes('Yes');
                    const hasNo = value.includes('No');
                    if (hasYes && !hasNo) backendValue = true;
                    else if (!hasYes && hasNo) backendValue = false;
                    // If both or neither, maybe default or send both? 
                    // Usually filters are "inclusive".
                    // If multiple values selected, we might need special handling.
                    // For now, let's pass the array of 'Yes'/'No' and hope backend handles it or expects it.
                    // Or re-map back to true/false
                     backendValue = value.map(v => {
                        if (String(v).toLowerCase() === 'yes') return true;
                        if (String(v).toLowerCase() === 'no') return false;
                        return v;
                     });
                }
                mapped[backendKey] = backendValue;
            });
            
            return mapped;
        },

        async refreshData(options = {}) {
            const { forceRefresh = false, pageInput, isPagination = false } = options;
            
            this.loading = true;
            if (!isPagination) {
                // Only reset selections on filter change / manual refresh, not on page navigation
                this.selectedIds = [];
                this.selectionResetKey++;
            }
            
            // Determine page
            let targetPage = this.page;
            if (pageInput) {
                targetPage = typeof pageInput === 'object' ? (pageInput.page || 1) : pageInput;
                this.page = targetPage;
            }
            
            // API expects 0-based index
            const apiPage = Math.max(0, this.page - 1);

            const ensureArray = (val) => {
                if (!val || val === 'all') return undefined;
                return Array.isArray(val) ? val : [val];
            };

            // Construct filter object
            const filter = {
                cycletype: 'Actual',
                year: this.globalFilterState.selectedyear,
                period: this.globalFilterState.selectedperiod,
                role: this.globalFilterState.selectedrole,
                network: ensureArray(this.globalFilterState.selectednetwork),
                region: ensureArray(this.globalFilterState.selectedregion),
                country: ensureArray(this.globalFilterState.selectedcountry),
                reportingunit: ensureArray(this.globalFilterState.selectedreportingunit),
                ruTypes: ensureArray(this.globalFilterState.selectedReportingUnitType),
                page: apiPage,
                limit: this.perpage,
                ...this.getMappedTableFilters()
            };

            if (this.sortKey) {
                // Map sort key if needed
                 const keyMap = {
                    'allUsersSet': 'allusersset',
                    'materialityActual': 'materialityset_actual',
                    'materialityForecast': 'materialityset_forecast',
                    'localcontrol_proforma_pl': 'localcontrol_proforma',
                    'localmanagement_proforma_pl': 'localmanagement_proforma',
                    'regionalcontrol_proforma_pl': 'regionalcontrol_proforma',
                    'regionalmanagement_proforma_pl': 'regionalmanagement_proforma',
                    'localcontrol_forecast_pl': 'localcontrol_forecast',
                    'localmanagement_forecast_pl': 'localmanagement_forecast',
                    'regionalcontrol_forecast_pl': 'regionalcontrol_forecast',
                    'regionalmanagement_forecast_pl': 'regionalmanagement_forecast',
                    'autoSubmissionEnabled': 'auto_regional_commentary_submission_enabled'
                };
                filter.sortBy = keyMap[this.sortKey] || this.sortKey;
                filter.sortDesc = this.sortDesc;
            }

            // Clean up null/undefined/"all"
            Object.keys(filter).forEach(key => {
                if (filter[key] === 'all' || filter[key] === null || filter[key] === undefined || filter[key] === '') {
                    delete filter[key];
                }
            });
            
            try {
                // Get client ID from store
                const client = this.$store.state.client;
                
                await this.fetchMyReportingUnits({ 
                    filter: filter, 
                    client: client,
                    axiosInstance: this.axios,
                    forceRefresh: forceRefresh
                });
                
                // Fetch materiality data after reporting units
                await this.fetchMaterialityData(filter, client, forceRefresh);
            } catch (error) {
                console.error("Failed to load workflow data", error);
                // Trigger global error feedback
                this.$store.commit('sbError', 'Failed to refresh data', { root: true });
            } finally {
                this.loading = false;
            }
        },
        async fetchMaterialityData(filter, client, forceRefresh = false) {
            this.materialityLoading = true;
            try {
                await this.fetchMateriality({ 
                    filter: filter, 
                    client: client,
                    axiosInstance: this.axios,
                    forceRefresh: forceRefresh
                });
            } catch (error) {
                // Error already handled in store
            } finally {
                this.materialityLoading = false;
            }
        },

        handleFilterChange(data) {
            // Ignore loading events
            if (data.key === 'loading') return;

            // Only track keys relevant for the API call to avoid performance issues
            const relevantKeys = [
                'selectedyear', 'selectedperiod', 'selectedrole',
                'selectednetwork', 'selectedregion', 'selectedcountry',
                'selectedreportingunit', 'selectedReportingUnitType'
            ];

            if (relevantKeys.includes(data.key)) {
                this.$set(this.globalFilterState, data.key, data.value);
            }
            
            // Track when essential filters are available
            if (relevantKeys.includes(data.key)) {
                const hasEssentialFilters = this.globalFilterState.selectedyear && 
                                             this.globalFilterState.selectedperiod &&
                                             this.globalFilterState.selectednetwork !== null &&
                                             this.globalFilterState.selectedregion !== null &&
                                             this.globalFilterState.selectedcountry !== null &&
                                             this.globalFilterState.selectedreportingunit !== null;
                
                if (hasEssentialFilters && !this.filtersReady) {
                    this.filtersReady = true;
                    
                    // Trigger initial load if this is the first time filters are ready
                    if (this.pendingInitialLoad) {
                        this.pendingInitialLoad = false;
                        this.refreshData();
                        return;
                    }
                }
            }

            if (data.reload) {
                // Debounce refresh to prevent multiple calls
                if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
                this.filterDebounceTimer = setTimeout(() => {
                    this.refreshData();
                }, 300);
            }
        }
    }
}
</script>